import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";

// components/reusable core elements:
import colors from "core/colors";
import LoadingSpinner from "core/LoadingSpinner";
import BodyText from "core/BodyText";
import Heading from "core/Heading";
import FormButtons from "core/CheckoutForm/FormButtons";

// styles:
import { GeneralFormError } from "core/CheckoutForm/styles";
import {
    SummaryContainerItem,
    SummaryContainerChild,
    InlineBodyText,
    SummaryBalanceDetails
} from "./styles";

// queries & mutations:
import { GET_PREPAID_BALANCE } from "./queries";

// reusable data / helpers:
import { getFormattedData, weDontNeedThisFunc } from "./helpers";
import { getCsrfTokenFromCookie } from "helpers/csrfToken";
import { ICON_DETAIL_PAGE } from "data/links";
import { PRO_PREPAID_DOWNLOAD, QUERY_PURCHASED } from "icons/helpers";
import { useAuth } from "frontend/hooks/useAuth";
import {
    TRACK_ITEM_ICON,
    TRACK_PURCHASE_PREPAID,
    trackViewCheckout,
    trackPurchase
} from "core/trackEvent";
import { SINGLE_ICON_PRICE } from "core/constants";

const PrepaidCheckout = ({ title, thumbnail, slugWithId, iconId }) => {
    const [formLoading, setFormLoading] = useState(false);
    const [showError, setShowError] = useState(null);

    const { loading, data: { currentUser = {} } = {} } = useQuery(
        GET_PREPAID_BALANCE,
        {
            notifyOnNetworkStatusChange: true
        }
    );

    const {
        hasPrepaidBalance,
        totalPrepaidBalanceAvailableCents,
        hasSubscription
    } = currentUser || {};

    useEffect(() => {
        // If user had bookmarked page and does not have prepaid balance OR a subscription
        // send them back to Icon detail
        if (!hasPrepaidBalance || hasSubscription) {
            window.location.replace(
                ICON_DETAIL_PAGE.replace(":slugWithId", slugWithId)
            );
        }
    }, []);

    const trackingData = {
        itemType: TRACK_ITEM_ICON,
        itemId: iconId,
        itemName: title,
        purchaseType: TRACK_PURCHASE_PREPAID,
        price: SINGLE_ICON_PRICE
    };
    useEffect(() => {
        trackViewCheckout(trackingData);
    }, []);

    if (loading) {
        return <LoadingSpinner />;
    }

    const {
        currentPrepaidBalance,
        individualPrice,
        availableIcons,
        balanceAfterPurchase,
        submitText,
        cancelText,
        iconDetailLink
    } = getFormattedData({
        slugWithId,
        hasPrepaidBalance,
        totalPrepaidBalanceAvailableCents
    });

    const submitPrepaidPurchase = async e => {
        e.preventDefault();

        setFormLoading(true);

        const routeURL = `/api/private/icons/${iconId}/redeemPrepaidBalance/`;

        const body = {
            expectedPriceCents: window._nounConfig.singleIconPriceCents
        };

        const options = {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "X-CSRFToken": getCsrfTokenFromCookie()
            },
            body: JSON.stringify(body)
        };

        const response = await fetch(routeURL, options);

        if (!response.ok) {
            setFormLoading(false);
        }

        response.json().then(data => {
            if (data.nonFieldErrors) {
                setFormLoading(false);
                setShowError(data.nonFieldErrors[0]);
            } else {
                trackPurchase(trackingData);

                const iconNavURL =
                    ICON_DETAIL_PAGE.replace(":slugWithId", slugWithId) +
                    `?${QUERY_PURCHASED}=${PRO_PREPAID_DOWNLOAD}`;
                window.location.replace(iconNavURL);
            }
        });
    };

    return (
        <>
            <SummaryContainerItem
                alignItems="center"
                justifyContent="space-between"
            >
                <SummaryContainerChild>
                    <BodyText
                        type="span"
                        size="md"
                        copyColor={colors.secondary.orca}
                    >
                        Current prepaid balance
                    </BodyText>
                    <InlineBodyText
                        type="span"
                        size="sm"
                        copyColor={colors.secondary.oolong}
                    >
                        {availableIcons} icons
                    </InlineBodyText>
                </SummaryContainerChild>
                <SummaryContainerChild>
                    <Heading
                        type="span"
                        size="xs"
                        headingColor={colors.secondary.orca}
                    >
                        ${currentPrepaidBalance}
                    </Heading>
                </SummaryContainerChild>
            </SummaryContainerItem>

            <SummaryBalanceDetails
                thumbnail={thumbnail}
                itemTitle={title}
                itemPriceOrderSummaryItem={`-$${individualPrice}`}
                orderSummaryTextLine1={title}
                orderSummaryTextLine2="SVG, PNG"
            />

            <SummaryContainerItem
                alignItems="center"
                justifyContent="space-between"
            >
                <SummaryContainerChild>
                    <BodyText
                        type="span"
                        size="md"
                        copyColor={colors.secondary.orca}
                    >
                        Balance after use
                    </BodyText>
                </SummaryContainerChild>
                <SummaryContainerChild>
                    <InlineBodyText
                        type="span"
                        size="sm"
                        copyColor={colors.secondary.oolong}
                    >
                        USD
                    </InlineBodyText>
                    <Heading
                        type="span"
                        size="xs"
                        headingColor={colors.secondary.orca}
                    >
                        ${balanceAfterPurchase}
                    </Heading>
                </SummaryContainerChild>
            </SummaryContainerItem>

            {showError && <GeneralFormError>{showError}</GeneralFormError>}

            <FormButtons
                showSavedCard={false}
                isSubmitDisabled={false} // disable this if hasPrepaidBalance === false
                submitText={submitText}
                cancelText={cancelText}
                cancelLink={iconDetailLink}
                submitFormWithNewCardInfo={submitPrepaidPurchase}
                submitFormWithSavedCardInfo={weDontNeedThisFunc}
                loading={formLoading}
            />
        </>
    );
};

PrepaidCheckout.propTypes = {
    thumbnail: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    slugWithId: PropTypes.string.isRequired
};

export default PrepaidCheckout;
